'use client'

import { useMemo, useState } from 'react'
import type { CSSProperties } from 'react'
import Link from 'next/link'
import './track-trace-case-study.css'

/**
 * Logistics Track & Trace System case study.
 *
 * A port of `public/radar-branding/track-trace-new.html`. That file is the
 * design contract: markup, class names, copy and section order match it, so
 * the two can be diffed. Styling lives in the sibling
 * `track-trace-case-study.css`, scoped under `.ltt-cs`.
 *
 * Renders full-bleed -- `WorkPage`'s hero and container are skipped for this
 * slug (see `fullBleedProjects` in ./index.ts) because it ships its own hero.
 *
 * The source drove the control tower with an IIFE writing innerHTML. Here the
 * filtering and derived state are pure and everything renders from state; the
 * numbers are unchanged.
 *
 * Every shipment is synthetic. The source labels it throughout ("Demo",
 * "synthetic event stream", "no account connection") and that copy is carried
 * over verbatim -- it is what keeps the demo honest.
 *
 * Dropped: its own header, footer, contact strip, closing CTA and prev/next
 * nav (site chrome renders those), its scroll-progress bar (the root layout
 * has `ScrollProgress`), and the "Project Details Coming Soon" section.
 */

interface Shipment {
  id: string
  origin: string
  destination: string
  mode: 'Sea' | 'Air' | 'Road'
  carrier: string
  status: string
  progress: number
  eta: string
  delay: number
  step: number
  events: [string, string][]
}

/** Synthetic manifest -- invented references, lanes and carriers. */
const SHIPMENTS: Shipment[] = [
  {
    id: 'PMJ-24081',
    origin: 'Manila',
    destination: 'Cebu',
    mode: 'Sea',
    carrier: 'Island Freight',
    status: 'In transit',
    progress: 72,
    eta: 'Aug 14 · 16:30',
    delay: 0,
    step: 3,
    events: [
      ['14:18', 'Position event received · Verde Passage'],
      ['12:04', 'Linehaul milestone confirmed'],
      ['08:31', 'Departed Manila South Harbor'],
    ],
  },
  {
    id: 'PMJ-24082',
    origin: 'Shenzhen',
    destination: 'Manila',
    mode: 'Sea',
    carrier: 'Pacific Container',
    status: 'Customs review',
    progress: 48,
    eta: 'Aug 15 · 11:00',
    delay: 9,
    step: 2,
    events: [
      ['14:22', 'Document review requested'],
      ['13:40', 'Customs hold opened · synthetic'],
      ['06:12', 'Arrival notice received'],
    ],
  },
  {
    id: 'PMJ-24083',
    origin: 'Davao',
    destination: 'Cagayan de Oro',
    mode: 'Road',
    carrier: 'Mindanao Linehaul',
    status: 'Out for delivery',
    progress: 89,
    eta: 'Aug 13 · 18:40',
    delay: 1,
    step: 4,
    events: [
      ['14:10', 'Final-mile vehicle assigned'],
      ['12:55', 'Departed destination hub'],
      ['10:38', 'Proof-of-sort scan received'],
    ],
  },
  {
    id: 'PMJ-24084',
    origin: 'Singapore',
    destination: 'Manila',
    mode: 'Air',
    carrier: 'Meridian Air Cargo',
    status: 'Arrived hub',
    progress: 64,
    eta: 'Aug 14 · 09:15',
    delay: 0,
    step: 3,
    events: [
      ['13:56', 'Arrived destination air hub'],
      ['11:20', 'Flight movement confirmed'],
      ['07:46', 'Export clearance complete'],
    ],
  },
  {
    id: 'PMJ-24085',
    origin: 'Cebu',
    destination: 'Iloilo',
    mode: 'Sea',
    carrier: 'Visayas Coastal',
    status: 'Gate out',
    progress: 32,
    eta: 'Aug 15 · 14:20',
    delay: 4,
    step: 1,
    events: [
      ['13:49', 'Gate-out event posted'],
      ['12:15', 'Schedule variance detected · +4h'],
      ['08:03', 'Container received at origin'],
    ],
  },
  {
    id: 'PMJ-24086',
    origin: 'Manila',
    destination: 'Batangas',
    mode: 'Road',
    carrier: 'South Corridor',
    status: 'Delivered',
    progress: 100,
    eta: 'Aug 13 · 10:12',
    delay: -1,
    step: 5,
    events: [
      ['10:12', 'Proof of delivery captured'],
      ['09:43', 'Arrived delivery point'],
      ['07:35', 'Departed local hub'],
    ],
  },
  {
    id: 'PMJ-24087',
    origin: 'Hong Kong',
    destination: 'Clark',
    mode: 'Air',
    carrier: 'Northstar Air',
    status: 'Linehaul',
    progress: 56,
    eta: 'Aug 14 · 05:50',
    delay: 2,
    step: 2,
    events: [
      ['14:01', 'In-flight status received'],
      ['11:14', 'Departed origin airport'],
      ['09:00', 'Cargo acceptance complete'],
    ],
  },
  {
    id: 'PMJ-24088',
    origin: 'General Santos',
    destination: 'Manila',
    mode: 'Sea',
    carrier: 'Archipelago Cargo',
    status: 'Port loading',
    progress: 18,
    eta: 'Aug 17 · 20:00',
    delay: 0,
    step: 1,
    events: [
      ['13:33', 'Load plan confirmed'],
      ['10:20', 'Export documents accepted'],
      ['08:44', 'Origin receipt posted'],
    ],
  },
  {
    id: 'PMJ-24089',
    origin: 'Subic',
    destination: 'Laguna',
    mode: 'Road',
    carrier: 'Central Link',
    status: 'Exception',
    progress: 41,
    eta: 'Aug 14 · 19:30',
    delay: 13,
    step: 2,
    events: [
      ['14:26', 'Vehicle exception escalated'],
      ['13:58', 'Replacement unit requested'],
      ['12:42', 'Linehaul movement paused'],
    ],
  },
  {
    id: 'PMJ-24090',
    origin: 'Bangkok',
    destination: 'Cebu',
    mode: 'Air',
    carrier: 'ASEAN Air Freight',
    status: 'At origin',
    progress: 12,
    eta: 'Aug 15 · 07:30',
    delay: 0,
    step: 0,
    events: [
      ['13:25', 'Cargo acceptance confirmed'],
      ['11:02', 'Booking validation passed'],
      ['09:16', 'Shipment record created'],
    ],
  },
]

const MILESTONES = [
  'Booked',
  'Origin gate',
  'Linehaul',
  'Destination hub',
  'Delivered',
]

/** Delay bands drive both the row colour and the label. */
const riskOf = (s: Shipment) =>
  s.delay > 8
    ? 'ltt-alert'
    : s.delay > 2
      ? 'ltt-watch'
      : s.status === 'Delivered'
        ? 'ltt-done'
        : 'ltt-ok'
const statusLabel = (s: Shipment) =>
  s.delay > 8 ? 'Exception' : s.delay > 2 ? 'Watch' : s.status
const deltaLabel = (d: number) =>
  d > 0 ? `+${d}h` : d < 0 ? `${d}h` : 'On time'
const pad = (n: number) => String(n).padStart(2, '0')

export default function TrackTraceProjectContent() {
  const [statusFilter, setStatusFilter] = useState('all')
  const [mode, setMode] = useState('all')
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState('PMJ-24081')

  const items = useMemo(
    () =>
      SHIPMENTS.filter(
        s =>
          (statusFilter === 'all' ||
            (statusFilter === 'active' && s.status !== 'Delivered') ||
            (statusFilter === 'risk' && s.delay > 2) ||
            (statusFilter === 'delivered' && s.status === 'Delivered')) &&
          (mode === 'all' || s.mode === mode) &&
          `${s.id} ${s.origin} ${s.destination}`.toLowerCase().includes(search)
      ),
    [statusFilter, mode, search]
  )

  const focusId =
    items.length && !items.some(s => s.id === selected) ? items[0].id : selected
  const focus = SHIPMENTS.find(s => s.id === focusId) ?? SHIPMENTS[0]

  const kpis = useMemo(() => {
    const active = items.filter(s => s.status !== 'Delivered').length
    const on = items.filter(s => s.delay <= 2).length
    return {
      visible: pad(items.length),
      active: pad(active),
      ontime: items.length ? `${Math.round((on / items.length) * 100)}%` : '—',
      risk: pad(items.filter(s => s.delay > 2).length),
      exceptions: pad(items.filter(s => s.delay > 8).length),
    }
  }, [items])

  return (
    <div className='ltt-cs'>
      <section className='ltt-hero' id='overview'>
        <div className='ltt-wrap ltt-hero-grid'>
          <div className='ltt-hero-copy'>
            <div className='ltt-badges'>
              <span className='ltt-badge'>Enterprise SaaS</span>
              <span className='ltt-badge'>2025</span>
            </div>
            <h1 className='ltt-display'>Logistics Track & Trace System</h1>
            <p className='ltt-hero-lead'>
              Enterprise logistics platform that unified fragmented operations
              into one real-time shipment record, with carrier and warehouse
              events normalized across every handoff and exception.
            </p>
            <div className='ltt-hero-actions'>
              <a className='ltt-btn ltt-btn-primary' href='#control-tower'>
                Explore mock control tower <span aria-hidden='true'>↓</span>
              </a>
              <Link className='ltt-btn' href='/projects'>
                View all projects <span aria-hidden='true'>↗</span>
              </Link>
            </div>
            <div className='ltt-tech' aria-label='Technologies'>
              <span>React</span>
              <span>TypeScript</span>
              <span>Node.js</span>
              <span>PostgreSQL</span>
              <span>Redis</span>
              <span>WebSockets</span>
            </div>
            <p className='ltt-claim-note'>
              <strong>Evidence boundary:</strong> this public case study
              demonstrates the architecture and workflow. It does not assert
              performance outcomes without published baselines.
            </p>
          </div>
          <div className='ltt-hero-visual'>
            <figure className='ltt-route-plate' aria-labelledby='route-caption'>
              <div className='ltt-plate-head'>
                <strong>
                  <i className='ltt-pulse-dot'></i>Shipment event rail
                </strong>
                <span>Mock network · PH / APAC</span>
              </div>
              <div className='ltt-route-stage-head' aria-hidden='true'>
                <span>Shipment</span>
                <span>Origin</span>
                <span>Gate</span>
                <span>Linehaul</span>
                <span>Hub</span>
                <span>Delivery</span>
              </div>
              <div className='ltt-route-lanes'>
                <div
                  className='ltt-route-lane'
                  style={
                    {
                      '--lane': 'var(--l-cyan)',
                      '--lane-ink': 'var(--l-cyan-ink)',
                      '--p': '68%',
                      '--delay': '0s',
                    } as CSSProperties
                  }
                >
                  <div className='ltt-lane-meta'>
                    <strong>PMJ-24081</strong>
                    <span>MNL → CEB</span>
                  </div>
                  <div className='ltt-lane-track'>
                    <i className='ltt-lane-base'></i>
                    <i className='ltt-lane-fill'></i>
                    <i className='ltt-packet'></i>
                    <div className='ltt-lane-nodes'>
                      <i className='ltt-lane-node ltt-done'></i>
                      <i className='ltt-lane-node ltt-done'></i>
                      <i className='ltt-lane-node ltt-done'></i>
                      <i className='ltt-lane-node ltt-current'></i>
                      <i className='ltt-lane-node'></i>
                    </div>
                    <span className='ltt-lane-status'>
                      In transit · on time
                    </span>
                  </div>
                </div>
                <div
                  className='ltt-route-lane'
                  style={
                    {
                      '--lane': 'var(--l-yellow)',
                      '--lane-ink': 'var(--l-yellow-ink)',
                      '--p': '48%',
                      '--delay': '1.1s',
                    } as CSSProperties
                  }
                >
                  <div className='ltt-lane-meta'>
                    <strong>PMJ-24082</strong>
                    <span>SZX → MNL</span>
                  </div>
                  <div className='ltt-lane-track ltt-lane-exception'>
                    <i className='ltt-lane-base'></i>
                    <i className='ltt-lane-fill'></i>
                    <i className='ltt-packet'></i>
                    <div className='ltt-lane-nodes'>
                      <i className='ltt-lane-node ltt-done'></i>
                      <i className='ltt-lane-node ltt-done'></i>
                      <i className='ltt-lane-node ltt-current'></i>
                      <i className='ltt-lane-node'></i>
                      <i className='ltt-lane-node'></i>
                    </div>
                    <span className='ltt-lane-status'>Customs · +9h</span>
                  </div>
                </div>
                <div
                  className='ltt-route-lane'
                  style={
                    {
                      '--lane': 'var(--l-mint)',
                      '--lane-ink': 'var(--l-mint-ink)',
                      '--p': '88%',
                      '--delay': '2.2s',
                    } as CSSProperties
                  }
                >
                  <div className='ltt-lane-meta'>
                    <strong>PMJ-24083</strong>
                    <span>DVO → CGY</span>
                  </div>
                  <div className='ltt-lane-track'>
                    <i className='ltt-lane-base'></i>
                    <i className='ltt-lane-fill'></i>
                    <i className='ltt-packet'></i>
                    <div className='ltt-lane-nodes'>
                      <i className='ltt-lane-node ltt-done'></i>
                      <i className='ltt-lane-node ltt-done'></i>
                      <i className='ltt-lane-node ltt-done'></i>
                      <i className='ltt-lane-node ltt-done'></i>
                      <i className='ltt-lane-node ltt-current'></i>
                    </div>
                    <span className='ltt-lane-status'>
                      Final mile · on time
                    </span>
                  </div>
                </div>
                <div
                  className='ltt-route-lane'
                  style={
                    {
                      '--lane': 'var(--l-red)',
                      '--lane-ink': 'var(--l-red-ink)',
                      '--p': '41%',
                      '--delay': '3.3s',
                    } as CSSProperties
                  }
                >
                  <div className='ltt-lane-meta'>
                    <strong>PMJ-24089</strong>
                    <span>SFS → LAG</span>
                  </div>
                  <div className='ltt-lane-track ltt-lane-exception'>
                    <i className='ltt-lane-base'></i>
                    <i className='ltt-lane-fill'></i>
                    <i className='ltt-packet'></i>
                    <div className='ltt-lane-nodes'>
                      <i className='ltt-lane-node ltt-done'></i>
                      <i className='ltt-lane-node ltt-done'></i>
                      <i className='ltt-lane-node ltt-current'></i>
                      <i className='ltt-lane-node'></i>
                      <i className='ltt-lane-node'></i>
                    </div>
                    <span className='ltt-lane-status'>Exception · +13h</span>
                  </div>
                </div>
              </div>
              <div className='ltt-plate-kpis'>
                <div className='ltt-plate-kpi'>
                  <span>Active shipments</span>
                  <strong>09</strong>
                </div>
                <div className='ltt-plate-kpi'>
                  <span>On-time sample</span>
                  <strong>70%</strong>
                </div>
                <div className='ltt-plate-kpi'>
                  <span>Open exceptions</span>
                  <strong>02</strong>
                </div>
              </div>
              <figcaption className='ltt-plate-foot' id='route-caption'>
                <span>Demo · synthetic shipment data</span>
                <span>Illustrative values only</span>
              </figcaption>
            </figure>
          </div>
        </div>
      </section>

      <section
        className='ltt-claim-strip'
        aria-label='Project architecture summary'
      >
        <div className='ltt-wrap ltt-claim-grid'>
          <div className='ltt-claim-intro'>
            <p className='ltt-label'>Project architecture summary</p>
            <p>
              Demonstrated capabilities from the case study. Mock dashboard
              values are illustrative and are not project results.
            </p>
          </div>
          <div className='ltt-claim-cell'>
            <strong>1</strong>
            <span>Normalized shipment record across every handoff</span>
          </div>
          <div className='ltt-claim-cell'>
            <strong>Live</strong>
            <span>Exception visibility from event to resolution</span>
          </div>
          <div className='ltt-claim-cell'>
            <strong>6</strong>
            <span>Technologies in the published stack</span>
          </div>
        </div>
      </section>

      <section className='ltt-section'>
        <div className='ltt-wrap'>
          <div className='ltt-section-head'>
            <div>
              <p className='ltt-label'>The operating shift</p>
              <h2 className='ltt-display'>One record through every handoff.</h2>
            </div>
            <p className='ltt-section-copy'>
              The interface concept turns fragmented updates into a shared event
              trail. Operations sees what moved, what changed, and what needs
              intervention without chasing carrier portals or status messages.
            </p>
          </div>
          <div className='ltt-handoff-board'>
            <article className='ltt-handoff-side'>
              <p className='ltt-label'>Before · fragmented operations</p>
              <h3>Every handoff resets the search.</h3>
              <ul className='ltt-handoff-list'>
                <li>
                  <i>01</i>
                  <span>Carrier portal holds one status.</span>
                </li>
                <li>
                  <i>02</i>
                  <span>Email holds the exception context.</span>
                </li>
                <li>
                  <i>03</i>
                  <span>Spreadsheet holds the customer promise.</span>
                </li>
                <li>
                  <i>04</i>
                  <span>Operations reconciles them manually.</span>
                </li>
              </ul>
            </article>
            <div className='ltt-handoff-arrow' aria-hidden='true'>
              →
            </div>
            <article className='ltt-handoff-side'>
              <p className='ltt-label'>After · unified event record</p>
              <h3>One shipment. One operational truth.</h3>
              <ul className='ltt-handoff-list'>
                <li>
                  <i>01</i>
                  <span>Events normalize into a shared timeline.</span>
                </li>
                <li>
                  <i>02</i>
                  <span>Delay risk is visible beside the route.</span>
                </li>
                <li>
                  <i>03</i>
                  <span>Every handoff keeps context attached.</span>
                </li>
                <li>
                  <i>04</i>
                  <span>Exceptions rise before routine updates.</span>
                </li>
              </ul>
            </article>
          </div>
        </div>
      </section>

      <section
        className='ltt-section'
        id='control-tower'
        aria-labelledby='tower-heading'
      >
        <div className='ltt-wrap'>
          <div className='ltt-section-head'>
            <p className='ltt-label'>Interactive product concept</p>
            <h2 className='ltt-display' id='tower-heading'>
              The shipment control tower.
            </h2>
            <p className='ltt-microcopy'>
              A redesigned demo dashboard · fixed synthetic dataset · no account
              connection
            </p>
          </div>

          <div className='ltt-tower-shell'>
            <div className='ltt-tower-top'>
              <div className='ltt-tower-title'>
                <span className='ltt-demo-flag'>Demo</span>
                <strong>Track &amp; Trace Control Tower</strong>
              </div>
              <p>Synthetic event stream · Updated Aug 13, 2026 14:32 PHT</p>
            </div>

            <div className='ltt-tower-toolbar'>
              <div className='ltt-toolbar-group'>
                <span>Status view</span>
                <div className='ltt-segments'>
                  {[
                    ['all', 'All'],
                    ['active', 'Active'],
                    ['risk', 'At risk'],
                    ['delivered', 'Delivered'],
                  ].map(([value, label]) => (
                    <button
                      key={value}
                      className='ltt-segment'
                      type='button'
                      aria-pressed={statusFilter === value}
                      onClick={() => setStatusFilter(value)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <label className='ltt-toolbar-group'>
                <span>Mode</span>
                <select
                  className='ltt-field'
                  value={mode}
                  onChange={e => setMode(e.target.value)}
                >
                  <option value='all'>All modes</option>
                  <option value='Sea'>Sea</option>
                  <option value='Air'>Air</option>
                  <option value='Road'>Road</option>
                </select>
              </label>
              <label className='ltt-toolbar-group'>
                <span>Find shipment</span>
                <input
                  className='ltt-field'
                  type='search'
                  placeholder='Reference or city'
                  value={search}
                  onChange={e => setSearch(e.target.value.trim().toLowerCase())}
                />
              </label>
            </div>

            <div className='ltt-tower-kpis'>
              {[
                ['Visible shipments', kpis.visible, 'mock records'],
                ['Active', kpis.active, 'not delivered'],
                ['On-time', kpis.ontime, 'delay ≤ 2h'],
                ['At risk', kpis.risk, 'watch + exception'],
                ['Exceptions', kpis.exceptions, 'delay > 8h'],
              ].map(([label, value, note]) => (
                <div className='ltt-tower-kpi' key={label}>
                  <span>{label}</span>
                  <strong>{value}</strong>
                  <small>{note}</small>
                </div>
              ))}
            </div>

            <div className='ltt-tower-main'>
              <section
                className='ltt-manifest'
                aria-labelledby='manifest-title'
              >
                <div className='ltt-manifest-head'>
                  <strong id='manifest-title'>Shipment manifest</strong>
                  <span>
                    {items.length} {items.length === 1 ? 'record' : 'records'}
                  </span>
                </div>
                <div className='ltt-table-wrap'>
                  <table className='ltt-shipment-table'>
                    <thead>
                      <tr>
                        {[
                          'Reference',
                          'Lane',
                          'Mode',
                          'Status',
                          'Progress',
                          'ETA / Actual',
                          'Delta',
                        ].map(h => (
                          <th key={h}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {items.length ? (
                        items.map(s => (
                          <tr key={s.id} data-selected={s.id === focusId}>
                            <td>
                              <button
                                className='ltt-shipment-select'
                                type='button'
                                onClick={() => setSelected(s.id)}
                              >
                                {s.id}
                              </button>
                            </td>
                            <td>
                              {s.origin} → {s.destination}
                            </td>
                            <td>{s.mode}</td>
                            <td>
                              <span className={`ltt-status ${riskOf(s)}`}>
                                {statusLabel(s)}
                              </span>
                            </td>
                            <td className='ltt-progress-cell'>
                              {s.progress}%
                              <div className='ltt-bar'>
                                <i
                                  style={
                                    { '--w': `${s.progress}%` } as CSSProperties
                                  }
                                />
                              </div>
                            </td>
                            <td>{s.eta}</td>
                            <td>{deltaLabel(s.delay)}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td className='ltt-empty' colSpan={7}>
                            No synthetic shipments match these filters.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </section>

              <aside
                className='ltt-inspector'
                aria-labelledby='inspector-title'
              >
                <div className='ltt-inspector-head'>
                  <p className='ltt-label'>Selected shipment</p>
                  <h3 className='ltt-id' id='inspector-title'>
                    {focus.id}
                  </h3>
                  <div className='ltt-route-name'>
                    {focus.origin} → {focus.destination}
                  </div>
                </div>
                <div className='ltt-inspector-grid'>
                  {[
                    ['Mode', focus.mode],
                    ['Carrier', focus.carrier],
                    ['ETA / Actual', focus.eta],
                    [
                      'ETA delta',
                      focus.delay > 0
                        ? `+${focus.delay} hours`
                        : focus.delay < 0
                          ? `${Math.abs(focus.delay)} hour early`
                          : 'On time',
                    ],
                  ].map(([label, value]) => (
                    <div className='ltt-inspect-stat' key={label}>
                      <span>{label}</span>
                      <strong>{value}</strong>
                    </div>
                  ))}
                </div>
                <div className='ltt-milestones'>
                  <p className='ltt-label'>Route milestones</p>
                  <div>
                    {MILESTONES.map((name, i) => {
                      const done =
                        focus.step > i || focus.status === 'Delivered'
                      const current =
                        focus.step === i && focus.status !== 'Delivered'
                      return (
                        <div
                          key={name}
                          className={`ltt-milestone${done ? ' ltt-done' : current ? ' ltt-current' : ''}`}
                        >
                          <i className='ltt-milestone-dot' />
                          <div className='ltt-milestone-copy'>
                            <strong>{name}</strong>
                            <span>
                              {done
                                ? 'Event confirmed'
                                : current
                                  ? focus.status
                                  : 'Awaiting event'}
                            </span>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
                <div className='ltt-events'>
                  <p className='ltt-label'>Latest event trail</p>
                  <div>
                    {focus.events.map(([time, text]) => (
                      <div className='ltt-event' key={time + text}>
                        <time>{time}</time>
                        <span>{text}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </aside>
            </div>

            <div className='ltt-tower-foot'>
              <span>Selected · {focus.id}</span>
              <span>Synthetic data · not a live carrier integration</span>
            </div>
            <p className='ltt-sr-status' aria-live='polite'>
              {`Selected ${focus.id}, synthetic shipment from ${focus.origin} to ${focus.destination}. ${statusLabel(focus)} with ${focus.progress}% progress.`}
            </p>
          </div>
        </div>
      </section>

      <section className='ltt-section' id='architecture'>
        <div className='ltt-wrap'>
          <div className='ltt-section-head'>
            <div>
              <p className='ltt-label'>Signal architecture</p>
              <h2 className='ltt-display'>Events in. Exceptions out.</h2>
            </div>
            <p className='ltt-section-copy'>
              A conceptual view of how the published stack can turn source
              events into an operations-ready interface. This diagram
              demonstrates product logic, not production architecture
              verification.
            </p>
          </div>
          <div className='ltt-pipeline'>
            <article className='ltt-pipe-stage'>
              <span className='ltt-num'>01 · SOURCES</span>
              <h3>Carrier & warehouse events</h3>
              <p>
                Scans, milestones, proofs, and exception updates enter the
                record.
              </p>
              <code>API · EDI · WEBHOOK</code>
            </article>
            <article className='ltt-pipe-stage'>
              <span className='ltt-num'>02 · INGEST</span>
              <h3>Node.js event gateway</h3>
              <p>
                Inputs are validated and normalized into one event language.
              </p>
              <code>NODE.JS · TYPESCRIPT</code>
            </article>
            <article className='ltt-pipe-stage'>
              <span className='ltt-num'>03 · RECORD</span>
              <h3>Shipment state</h3>
              <p>
                Current state and event history stay joined to one reference.
              </p>
              <code>POSTGRESQL</code>
            </article>
            <article className='ltt-pipe-stage'>
              <span className='ltt-num'>04 · SIGNAL</span>
              <h3>Fast state updates</h3>
              <p>
                Operational changes reach connected views without a manual
                refresh loop.
              </p>
              <code>REDIS · WEBSOCKETS</code>
            </article>
            <article className='ltt-pipe-stage'>
              <span className='ltt-num'>05 · ACTION</span>
              <h3>Exception-first UI</h3>
              <p>
                Routine movement stays quiet; delayed handoffs move to the top.
              </p>
              <code>REACT</code>
            </article>
          </div>
        </div>
      </section>

      <section className='ltt-section'>
        <div className='ltt-wrap'>
          <div className='ltt-section-head'>
            <div>
              <p className='ltt-label'>Published technology stack</p>
              <h2 className='ltt-display'>
                Six pieces. One operating surface.
              </h2>
            </div>
            <p className='ltt-section-copy'>
              The stack below is retained exactly from the live project page.
              The role descriptions explain the redesign concept and should not
              be read as audited implementation details.
            </p>
          </div>
          <div className='ltt-tech-manifest'>
            <article className='ltt-tech-cell'>
              <span>01</span>
              <h3>React</h3>
              <p>Operations interface and shipment inspection views.</p>
            </article>
            <article className='ltt-tech-cell'>
              <span>02</span>
              <h3>TypeScript</h3>
              <p>Typed event contracts across interface and services.</p>
            </article>
            <article className='ltt-tech-cell'>
              <span>03</span>
              <h3>Node.js</h3>
              <p>Service layer for ingestion and integrations.</p>
            </article>
            <article className='ltt-tech-cell'>
              <span>04</span>
              <h3>PostgreSQL</h3>
              <p>Shipment state, history, and relational records.</p>
            </article>
            <article className='ltt-tech-cell'>
              <span>05</span>
              <h3>Redis</h3>
              <p>Fast access to operational state and event coordination.</p>
            </article>
            <article className='ltt-tech-cell'>
              <span>06</span>
              <h3>WebSockets</h3>
              <p>Connected status updates across the control tower.</p>
            </article>
          </div>
        </div>
      </section>
    </div>
  )
}
